import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { KeyFacts, ProTip } from '../../components/LessonUI';

const modernArrayMethodsCode = `// Functional Logic: Immutable Array Transformation (ES2023)
const series = [10, 5, 20, 15];

// 1. Immutable Sort: returns a new array, original is preserved
const ascending = series.toSorted((a, b) => a - b); 

// 2. Immutable Splicing: replace elements without mutation
const subset = series.toSpliced(1, 2, 99); 

// 3. Predicate Finding from Tail: O(n) but starts from end
const lastHighValue = series.findLast(n => n &gt; 12); // 15

// 4. The "with" operator: Immutable single-index update
const modified = series.with(2, 500); // [10, 5, 500, 15]`;

const logicalAssignmentCode = `// Defensive Engineering: Logical Assignment (ES2021)
let connectionPool = null;

// Nullish Coalescing Assignment: init ONLY if null/undefined
connectionPool ??= createNewPool(); 

let options = { debug: true, retry: 0 };

// Logical OR Assignment: init if falsy (0, "", null, etc.)
options.retry ||= 5; // options.retry becomes 5

// Logical AND Assignment: update ONLY if truthy
options.debug &&= checkSystemHealth();`;

const numericSeparatorsCode = `// Readability Engineering: Numeric Separators (ES2021)
// Essential for high-precision financial or scientific logic.

const ONE_BILLION = 1_000_000_000;
const MICRONS = 0.000_000_001;
const BIT_MASK = 0b1010_0001_1111;
const HEX_COLOR = 0xFF_AA_00;`;

const JSES2023 = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Modern Language Spec</div>
                <h1>Modern JS Features (ES2021ES2023)</h1>
                <p className="hero-lead">
                    JavaScript is evolving into a high-performance, functional-first language.
                    Explore the latest specifications that enable immutable data patterns,
                    cleaner logical control, and improved numeric readability.
                </p>
            </div>

            <div className="content-section">
                <h2>Immutable Array Patterns (ES2023)</h2>
                <p>
                    Historically, array methods like `sort()`, `reverse()`, and `splice()`
                    were **mutating**they changed the original array in place. While
                    efficient for memory, this caused bugs in state-driven frameworks
                    like React. The ES2023 "Change Array by Copy" proposal introduced
                    alternatives that return a fresh array, enabling clean functional
                    programming.
                </p>
                <CodeBlock code={modernArrayMethodsCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>Logical Assignment & Control Flow</h2>
                <p>
                    Modern JavaScript reduces boilerplate through **Logical Assignment
                    Operators**. These combine logical operators (`||`, `&&`, `??`)
                    with assignment (`=`), allowing you to conditionally initialize
                    or update variables in a single, atomic expression.
                </p>
                <CodeBlock code={logicalAssignmentCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>Precision & Readability</h2>
                <p>
                    As JavaScript is increasingly used for data processing and system
                    instrumentation, handling large numbers or bitmasks requires
                    visual clarity. **Numeric Separators** allow the use of underscores
                    to group digits without affecting the numeric value.
                </p>
                <CodeBlock code={numericSeparatorsCode} language="javascript" />
            </div>

            <ProTip><p>
                    While methods like `toSorted()` are safer for state management,
                    remember that they create a **shallow copy** of the array. For
                    massive datasets (100k+ elements), constant copying can lead to
                    increased Garbage Collection (GC) pressure. In performance-critical
                    hot paths, in-place mutation may still be the superior engineering
                    choice.
                </p></ProTip>

            <KeyFacts><ul>
                    <li>**Immutability:** Use `toSorted/toReversed` for predictable state transitions.</li>
                    <li>**Conciseness:** Use `??=` for default configuration initialization.</li>
                    <li>**Searchability:** Use `findLast` to locate most recent events in log streams.</li>
                    <li>**Calculations:** Always use `_` for numbers exceeding six digits to prevent human error.</li>
                    <li>**Readability:** Favor `.replaceAll()` over complex global regex for simple string swaps.</li>
                </ul></KeyFacts>

        </div>
    );
};

export default JSES2023;



